import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiMail, FiLock, FiUser, FiSun, FiMoon } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import { authAPI } from '../api/endpoints';
import { setUser, validateEmail, validatePassword } from '../utils/helpers';
import { showToast } from '../components/common/Toaster';
import logo from '../assets/images/logo.png';
import {
  LoginContainer,
  LoginCard,
  LoginLeft,
  LoginRight,
  Logo,
  LoginTitle,
  LoginSubtitle,
  LoginForm,
  Divider,
  SignupPrompt,
  ThemeToggle,
  BackgroundShape,
  FeatureList,
  FeatureItem,
  FeatureIcon,
} from './Login.styles';

const Register = () => {
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'parent',
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.username) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setLoading(true);
    
    try {
      const response = await authAPI.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      });
      
      const { user } = response.data;
      
      // Store user data (token is handled by cookies)
      setUser(user);
      
      // Show success toast
      showToast('success', 'Registration successful! Redirecting to your dashboard...', 3000);
      
      // Navigate after a delay
      setTimeout(() => {
        const roleRoutes = {
          student: '/learner',
          parent: '/parent',
          teacher: '/teacher',
          publisher: '/publisher',
          admin: '/admin',
          owner: '/owner',
        };
        
        navigate(roleRoutes[user.role] || '/learner');
      }, 2000);
    } catch (error) {
      setErrors({
        general: error.response?.data?.error || 'Registration failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <BackgroundShape className="shape-1" />
      <BackgroundShape className="shape-2" />
      <BackgroundShape className="shape-3" />
      
      <ThemeToggle onClick={toggleTheme}>
        {isDarkMode ? <FiSun /> : <FiMoon />}
      </ThemeToggle>
      
      <LoginCard>
        <LoginLeft>
          <Logo style={{ justifyContent: 'center' }}>
            <img src={logo} alt="Dira Learners Logo" style={{ width: '200px', height: 'auto' }} />
          </Logo>
          
          <FeatureList>
            <FeatureItem>
              <FeatureIcon>🎓</FeatureIcon>
              <div>
                <h4>Quality Education</h4>
                <p>Access CBC-aligned content from expert teachers</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>📱</FeatureIcon>
              <div>
                <h4>Learn Anywhere</h4>
                <p>Mobile-friendly platform for learning on the go</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>📊</FeatureIcon>
              <div>
                <h4>Track Progress</h4>
                <p>Monitor performance with detailed analytics</p>
              </div>
            </FeatureItem>
            
            <FeatureItem>
              <FeatureIcon>👨‍👩‍👧</FeatureIcon>
              <div>
                <h4>Parent Dashboard</h4>
                <p>Stay connected with your child's learning journey</p>
              </div>
            </FeatureItem>
          </FeatureList>
        </LoginLeft>
        
        <LoginRight>
          <LoginTitle>Create Account</LoginTitle>
          <LoginSubtitle>Join Dira Learners and start your learning journey</LoginSubtitle>
          
          {errors.general && (
            <div style={{ 
              padding: '12px', 
              backgroundColor: '#FEE2E2', 
              color: '#DC2626', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px'
            }}>
              {errors.general}
            </div>
          )}
          
          <LoginForm onSubmit={handleSubmit}>
            <Input
              label="Username"
              type="text"
              name="username"
              placeholder="Enter your username"
              value={formData.username}
              onChange={handleChange}
              error={errors.username}
              leftIcon={<FiUser />}
              required
            />
            
            <Input
              label="Email Address"
              type="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              leftIcon={<FiMail />}
              required
            />
            
            <Input
              label="Password"
              type="password"
              name="password"
              placeholder="Create a password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              leftIcon={<FiLock />}
              required
            />
            
            <Input
              label="Confirm Password"
              type="password"
              name="confirmPassword"
              placeholder="Confirm your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              leftIcon={<FiLock />}
              required
            />
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#374151'
              }}>
                Role
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '2px solid #E5E7EB',
                  borderRadius: '12px',
                  fontSize: '16px',
                  backgroundColor: '#F9FAFB',
                  outline: 'none',
                  transition: 'border-color 0.2s',
                }}
              >
                <option value="student">Student</option>
                <option value="parent">Parent</option>
                <option value="teacher">Teacher</option>
                <option value="publisher">Publisher</option>
                <option value="admin">Admin</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            
            <Button
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={loading}
            >
              Create Account
            </Button>
          </LoginForm>
          
          <Divider>
            <span>or</span>
          </Divider>
          
          <SignupPrompt>
            Already have an account? <Link to="/login">Sign in</Link>
          </SignupPrompt>
        </LoginRight>
      </LoginCard>
    </LoginContainer>
  );
};

export default Register;
